import React, { useState, useRef } from "react";
import InputField from "./inputField";

interface InputFieldProps {
  setStatusX: (status: boolean) => void;
}

/**
 * InputFieldDoc component.
 *
 * This component renders an input field for document numbers along with a select dropdown
 * for selecting the type of document. It validates the document number based on the selected
 * document type and displays an error message if the validation fails.
 *
 * @component
 * @example
 * return (
 *   <InputFieldDoc />
 * )
 *
 * @typedef {Object} InputFieldProps
 * @property {string} [error] - The error message to display if validation fails.
 *
const InputFieldDoc: React.FC<InputFieldProps> = ({ setStatusX }) => {
 */
const InputFieldDoc: React.FC<InputFieldProps> = ({ setStatusX }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const [error, setError] = useState("");

  /**
   * Validates the document based on the selected document type and its value.
   *
   * This function checks if the document value is empty and sets an error message if it is.
   * It then validates the document value based on the selected document type:
   * - "dni": Must be exactly 8 numeric digits.
   * - "ce": Must be an alphanumeric string between 8 and 12 characters.
   * - Default (e.g., "pasaporte"): Must be an alphanumeric string between 8 and 12 characters.
   *
   * If the validation fails, an appropriate error message is set and the function returns `false`.
   * If the validation passes, the function returns `true`.
   *
   * @returns {boolean} `true` if the document is valid, `false` otherwise.
   */
  const validarDocumento = () => {
    const tipoDoc = selectRef.current ? selectRef.current.value : "";
    const valorDoc = inputRef.current ? inputRef.current.value : "";

    setError("");

    switch (tipoDoc) {
      case "1":
        if (!/^\d{8}$/.test(valorDoc)) {
          setError("El DNI debe tener 8 dígitos numéricos");
          return false;
        }
        break;
      case "3":
        if (!/^[a-zA-Z0-9]{8,12}$/.test(valorDoc)) {
          setError(
            "El carnet de extranjería debe tener entre 8 y 12 caracteres alfanuméricos."
          );
          return false;
        }
        break;
      default:
        if (!/^[a-zA-Z0-9]{8,12}$/.test(valorDoc)) {
          setError(
            "El pasaporte debe tener entre 8 y 12 caracteres alfanuméricos."
          );
          return false;
        }
        break;
    }
    setError("Done");
    setStatusX(true);
    return true;
  };
  return (
    <div className="form_bx">
      <div
        id="forminputBorde"
        className="form_inpt"
        style={{ borderColor: error ? error !== "Done" ? "red" : "green" : "" }}
      >
        <div className="warp-select">
          <select
            id="tipodedoc"
            name="tipodedoc"
            data-name="Field 3"
            className="form_inpt-select w-select"
            ref={selectRef}
          >
            <option value="1">DNI</option>
            <option value="3">CE</option>
            <option value="9">PAS</option>
          </select>
        </div>
        <InputField
          id="documentonumero"
          dataName="Field 4"
          name="documentonumero"
          placeholder="Número de documento"
          type="text"
          maxLength={256}
          pattern="^[a-zA-Z0-9]+$"
          onBlur={validarDocumento}
          ref={inputRef}
        />
      </div>
      {error && error !== "Done" && (
        <div
          id="form_textalertdocu"
          className="form_text-alert"
          style={{ display: "block", color: "red" }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default InputFieldDoc;
